<script setup lang="ts">
import { computed } from 'vue'

import type { CreatorSegmentType } from '../../generated/social-media/metapicSocialMediaAPIDocumentation.schemas'
import FlexComponent from '../atoms/FlexComponent.vue'
import TypographyComponent from '../atoms/TypographyComponent.vue'
import Card from '../molecules/Card.vue'

const props = withDefaults(
  defineProps<{
    isInvalid?: boolean
    errorMessage?: string
    isDisabled?: boolean
  }>(),
  { isInvalid: false, errorMessage: 'Select a segment type', isDisabled: false },
)

const type = defineModel<CreatorSegmentType>()
const showError = computed(() => props.isInvalid && !type.value)
const groupName = `segment-type-${crypto.randomUUID()}`
const errorId = `${groupName}-error`

const TITLE = 'Segment type'

const ALL_OPTIONS: { value: CreatorSegmentType; label: string; description: string }[] = [
  {
    value: 'dynamic',
    label: 'Dynamic',
    description: 'Creators are evaluated continuously based on filter criteria. Matching creators are added automatically.',
  },
  {
    value: 'static',
    label: 'Static',
    description: "Filter + manual selection. The segment is locked on save and won't change.",
  },
]

const OPTIONS = computed(() => (props.isDisabled && type.value ? ALL_OPTIONS.filter((option) => option.value === type.value) : ALL_OPTIONS))
</script>

<template>
  <Card :title="TITLE">
    <FlexComponent direction="column" :gap="8">
      <FlexComponent
        direction="column"
        :gap="8"
        :role="isDisabled ? undefined : 'radiogroup'"
        :aria-label="TITLE"
        :aria-invalid="showError || undefined"
        :aria-describedby="showError ? errorId : undefined"
      >
        <component
          :is="isDisabled ? 'div' : 'label'"
          v-for="option in OPTIONS"
          :key="option.value"
          :class="[
            'segment-type-selector__option',
            {
              'segment-type-selector__option--selected': type === option.value,
              'segment-type-selector__option--locked': isDisabled,
              'segment-type-selector__option--invalid': showError,
            },
          ]"
        >
          <input
            v-if="!isDisabled"
            v-model="type"
            type="radio"
            :name="groupName"
            :value="option.value"
            class="segment-type-selector__radio"
          />
          <FlexComponent direction="column" :gap="4">
            <TypographyComponent variant="title-large" :font-weight="600">{{ option.label }}</TypographyComponent>
            <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ option.description }}</TypographyComponent>
          </FlexComponent>
        </component>
      </FlexComponent>

      <TypographyComponent v-if="showError" :id="errorId" role="alert" variant="label-small" color="var(--error)">
        {{ errorMessage }}
      </TypographyComponent>
    </FlexComponent>
  </Card>
</template>

<style scoped lang="scss">
.segment-type-selector__option {
  display: flex;
  align-items: flex-start;
  gap: 12px;

  padding: 16px;
  border: 1px solid var(--button-hover);
  border-radius: 8px;
  background: var(--inverted);

  cursor: pointer;
  transition: border-color var(--transition) ease;

  &:hover {
    border-color: var(--secondary-12);
  }
}

.segment-type-selector__option--selected {
  border-color: var(--button-hover);
  background: var(--item-hover);
}

.segment-type-selector__option--invalid {
  &,
  &:hover {
    border-color: var(--error);
  }

  &.segment-type-selector__option--locked {
    cursor: default;

    &:hover {
      border-color: var(--button-hover);
    }
  }

  .segment-type-selector__radio {
    accent-color: var(--error);
  }
}

.segment-type-selector__option--locked {
  cursor: default;

  &:hover {
    border-color: var(--button-hover);
  }
}

.segment-type-selector__radio {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin: 2px 0 0;
  accent-color: var(--primary);
  cursor: pointer;
}
</style>
